import { all, takeLatest, call, put, select } from 'redux-saga/effects';
import { navigate } from '@reach/router';
import { ActionType } from 'deox';
import { sortBy } from 'lodash';
import { executeAfterToast } from '@/utils';

import { Paths } from '@/pages/routers';
import { RootState } from '@/redux/reducers';
import {
  fetchAppsInfoActions,
  createAppInfoActions,
  editAppInfoActions,
  deleteAppInfoActions,
  scrapeAppInfoActions,
  createAndScrapeAppInfoActions,
} from '@/redux/actions/service-directory/app-info';
import { AppInfoInstance } from '@/services/service-directory/app-info';

export function* fetchAppsInfoSaga(action: ActionType<typeof fetchAppsInfoActions.request>) {
  try {
    const { entityId } = action.payload;
    const appsInfo = yield call(AppInfoInstance.fetchAppsInfo, entityId);
    yield put(fetchAppsInfoActions.success(sortBy(appsInfo, ['sequence'])));
  } catch (err) {
    yield put(fetchAppsInfoActions.failure(err));
  }
}

export function* createAppInfoSaga(action: ActionType<typeof createAppInfoActions.request>) {
  try {
    const { appInfo } = action.payload;
    const organisationId = yield select((state: RootState) => state.serviceDirectoryEntity.entityDashboard?.id);
    yield call(AppInfoInstance.createAppInfo, appInfo, organisationId);
    yield put(createAppInfoActions.success());

    // refetch apps
    yield put(fetchAppsInfoActions.request(organisationId));

    executeAfterToast(() => navigate(`${Paths.Dashboard}${Paths.MyApps}`));
  } catch (err) {
    yield put(createAppInfoActions.failure(err));
  }
}

export function* editAppInfoSaga(action: ActionType<typeof editAppInfoActions.request>) {
  try {
    const { id, appInfo } = action.payload;
    const organisationId = yield select((state: RootState) => state.serviceDirectoryEntity.entityDashboard?.id);

    yield call(AppInfoInstance.updateAppInfo, id, appInfo, organisationId);
    yield put(editAppInfoActions.success());

    // refetch api docs
    yield put(fetchAppsInfoActions.request(organisationId));

    executeAfterToast(() => navigate(`${Paths.Dashboard}${Paths.MyApps}`));
  } catch (err) {
    yield put(editAppInfoActions.failure(err));
  }
}

export function* deleteAppInfoSaga(action: ActionType<typeof deleteAppInfoActions.request>) {
  try {
    const { id } = action.payload;
    const organisationId = yield select((state: RootState) => state.serviceDirectoryEntity.entityDashboard?.id);

    yield call(AppInfoInstance.deleteAppInfo, id, organisationId);
    yield put(deleteAppInfoActions.success());

    // refetch api docs
    yield put(fetchAppsInfoActions.request(organisationId));

    executeAfterToast(() => navigate(`${Paths.Dashboard}${Paths.MyApps}`));
  } catch (err) {
    yield put(deleteAppInfoActions.failure(err));
  }
}

export function* scrapeAppInfoSaga(action: ActionType<typeof scrapeAppInfoActions.request>) {
  try {
    const { id, playStoreLink, appStoreLink } = action.payload;
    const organisationId = yield select((state: RootState) => state.serviceDirectoryEntity.entityDashboard?.id);
    yield call(AppInfoInstance.scrapeAppInfo, id, appStoreLink, playStoreLink);
    yield put(scrapeAppInfoActions.success());

    // refetch app info
    yield put(fetchAppsInfoActions.request(organisationId));
  } catch (err) {
    yield put(scrapeAppInfoActions.failure(err));
  }
}

export function* createAndScrapeAppInfoSaga(action: ActionType<typeof createAndScrapeAppInfoActions.request>) {
  try {
    const { urlScrape } = action.payload;
    const organisationId = yield select((state: RootState) => state.serviceDirectoryEntity.entityDashboard?.id);
    const data = yield call(AppInfoInstance.createAndScrapeAppInfo, organisationId, urlScrape);
    yield put(createAndScrapeAppInfoActions.success());
    // refetch app info
    yield put(fetchAppsInfoActions.request(organisationId));
    if (data?.id) {
      navigate(`/dashboard/my-apps/edit/${data?.id}`);
    }
  } catch (err) {
    yield put(createAndScrapeAppInfoActions.failure(err));
  }
}

export default function* root() {
  yield all([
    takeLatest(fetchAppsInfoActions.request.type, fetchAppsInfoSaga),
    takeLatest(createAppInfoActions.request.type, createAppInfoSaga),
    takeLatest(editAppInfoActions.request.type, editAppInfoSaga),
    takeLatest(deleteAppInfoActions.request.type, deleteAppInfoSaga),
    takeLatest(scrapeAppInfoActions.request.type, scrapeAppInfoSaga),
    takeLatest(createAndScrapeAppInfoActions.request.type, createAndScrapeAppInfoSaga),
  ]);
}
